"use client";

import {
  CustomButton,
  FlexCenter,
  FlexCol,
} from "@/components";
import ResultContent from "@/components/resultContent";
import { IBaseLabelValue } from "@/interfaces";
import {
  IRawPurchasedActiveLoansInquiryResData,
  TActiveLoanItem,
} from "@/interfaces/inquiries/bank";
import { cn } from "@/lib";
import { routes } from "@/routes";
import { useRouter } from "next/navigation";

const fieldLabels: Record<string, string> = {
  nationalCode: "کد ملی",
  name: "نام و نام خانوادگی",
  totalAmount: "مجموع مبلغ تسهیلات",
  debtTotalAmount: "مجموع تسهیلات مانده",
  pastExpiredTotalAmount: "مانده سررسید گذشته",
  deferredTotalAmount: "مانده معوق",
  suspiciousTotalAmount: "مانده مشکوک‌الوصول",
  dishonored: "مبلغ تسهیلات یا تعهدات برگشتی",
  bank: "بانک",
  bankName: "بانک",
  branch: "شعبه",
  branchName: "شعبه",
  amount: "مبلغ تسهیلات",
  installmentAmount: "مبلغ قسط",
  remainingAmount: "مانده بدهی",
  debtAmount: "مانده بدهی",
  loanNumber: "شماره تسهیلات",
  facilityNumber: "شماره تسهیلات",
  contractNumber: "شماره قرارداد",
  status: "وضعیت",
  type: "نوع تسهیلات",
  loanType: "نوع تسهیلات",
  startDate: "تاریخ شروع",
  endDate: "تاریخ پایان",
  dueDate: "سررسید",
  installmentsCount: "تعداد اقساط",
  remainingInstallments: "اقساط باقی‌مانده",
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "بله" : "خیر";
  if (typeof value === "number") return value.toLocaleString("fa-IR");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const getLoans = (
  payload: IRawPurchasedActiveLoansInquiryResData["data"]["data"],
): TActiveLoanItem[] => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const candidates = [
    payload.loans,
    payload.facilities,
    payload.items,
    payload.data as TActiveLoanItem[] | undefined,
  ];

  return candidates.find(Array.isArray) || [];
};

const buildSummary = (
  data: IRawPurchasedActiveLoansInquiryResData,
  loans: TActiveLoanItem[],
): IBaseLabelValue[] => {
  const info =
    data.data.data && !Array.isArray(data.data.data)
      ? (data.data.data.info as TActiveLoanItem | undefined)
      : undefined;
  const nationalCode =
    data.data.input?.national_code ||
    (info ? String(info.nationalCode || info.national_code || "") : "") ||
    (data.data.data && !Array.isArray(data.data.data)
      ? String(data.data.data.nationalCode || data.data.data.national_code || "")
      : "");

  return [
    {
      label: "کد ملی",
      value: nationalCode || "-",
    },
    {
      label: "تعداد تسهیلات فعال",
      value: String(
        loans.length ||
          (data.data.data && !Array.isArray(data.data.data)
            ? data.data.data.count || 0
            : 0),
      ),
    },
  ];
};

const buildInfoContent = (
  data: IRawPurchasedActiveLoansInquiryResData,
): IBaseLabelValue[] => {
  const info =
    data.data.data && !Array.isArray(data.data.data)
      ? (data.data.data.info as TActiveLoanItem | undefined)
      : undefined;

  if (!info || typeof info !== "object") return [];

  return Object.entries(info)
    .filter(([key]) => key !== "nationalCode" && key !== "national_code")
    .map(([key, value]) => ({
      label: fieldLabels[key] || key,
      value: formatValue(value),
    }));
};

const ActiveLoansInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedActiveLoansInquiryResData;
}) => {
  const router = useRouter();
  const loans = getLoans(data.data.data);
  const summary = buildSummary(data, loans);
  const infoContent = buildInfoContent(data);
  const message =
    data.data.message ||
    (data.data.data && !Array.isArray(data.data.data)
      ? String(data.data.data.message || "")
      : "");

  return (
    <div className="shadow-main from-emerald-mint relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[760px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-6 sm:p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          نتیجه استعلام تسهیلات فعال بانکی
        </h2>

        <ResultContent content={summary} />

        {infoContent.length ? (
          <>
            <h3 className="mb-4 font-semibold text-primary-main">
              اطلاعات تسهیلات
            </h3>
            <ResultContent content={infoContent} />
          </>
        ) : null}

        {loans.length ? (
          <div className="mb-8 space-y-4">
            {loans.map((loan, index) => {
              const content = Object.entries(loan)
                .filter(([, value]) => value !== null && value !== undefined && value !== "")
                .map(([key, value]) => ({
                  label: fieldLabels[key] || key,
                  value: formatValue(value),
                }));

              return (
                <div
                  key={index}
                  className="rounded-2xl border border-primary-main/15 bg-primary-main/5 p-4"
                >
                  <h3 className="mb-4 font-semibold text-primary-main">
                    تسهیلات شماره {index + 1}
                  </h3>
                  <ResultContent
                    content={content}
                    className="mb-0! shadow-none"
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mb-8 rounded-2xl border border-success-main/20 bg-success-main/10 p-5 text-center text-sm leading-8 text-success-main sm:text-base">
            {message || "برای این کد ملی تسهیلات فعال بانکی ثبت نشده است."}
          </div>
        )}

        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <CustomButton
            id="details-active-loans-back"
            className={cn("w-fit bg-gray-500")}
            onClick={() => router.push(routes.main.INQUIRY_HISTORY.primaryPath)}
          >
            بازگشت
          </CustomButton>
          <CustomButton
            id="details-active-loans-new-request"
            onClick={() => router.push(routes.services.Active_Loans.rewritePath)}
            className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
          >
            استعلام جدید
          </CustomButton>
        </FlexCenter>
      </FlexCol>
    </div>
  );
};

export default ActiveLoansInquiryDetails;
